import { Node } from "../../Utils/Collections/Node";
import { NodeList } from "../../Utils/Collections/NodeList";
import { Pool } from "../../Utils/Collections/Pool";
import { WrappedArray } from "../../Utils/Collections/WrapperArray";
import { TIMER } from "../Gameplay";
import { Stadium } from "./Stadium";



interface HitZoneMorph {
    /**
     * Index hit zony v brane
     */
    zoneId: number;
    /**
     * Vaha morfovani teto zony (0 az 1)
     */
    weight: number;
}



const enum eGoalWall {
    back,
    side
}



class MorphManager {
    /**
     * pc.MorphInstance pro mesh site
     */
    private _morphInstance: pc.MorphInstance;
    /**
     * Zony, ktere se morfuji
     */
    private _zones: WrappedArray<HitZoneMorph>;
    /**
     * Pomer vahy morfovani (0 az 1) (podle sily jakou mic narazil do site)
     */
    private _weightRatio: number;
    /**
     * Aktualni faze morfovani (0 = z 0 do 1, 1 = z 1 do 0, -1 = vypnuto)
     */
    private _phaseId: number;
    /**
     * Cas zahajeni faze
     */
    private _phaseTime: number;
    /**
     * (RO) Strana site, se kterou morf pracuje
     */
    get goalWall(): eGoalWall { return this._goalWall; }
    private _goalWall: eGoalWall;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(morphInstance: pc.MorphInstance) {
        this._morphInstance = morphInstance;
        this._zones = new WrappedArray<HitZoneMorph>();
        this._phaseId = -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    start(goalWall: eGoalWall, zones: HitZoneMorph[], weightRatio: number): void {
        if (this._phaseId >= 0)
            this.reset();

        zones.forEach((element: HitZoneMorph): void => { this._zones.add(element); });

        this._goalWall = goalWall;
        this._weightRatio = weightRatio;
        this._phaseId = 0;
        this._phaseTime = TIMER.time;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        if (!this._zones.empty) {
            this._zones.forEach((element: HitZoneMorph): void => {
                this._morphInstance.setWeight(element.zoneId, 0);
            });
            this._zones.clear(true);
        }

        this._phaseId = -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (this._phaseId < 0)
            return false;

        let morphPos: number;

        if (this._phaseId == 0) {
            // Smer z 0 do 1
            morphPos = (TIMER.time - this._phaseTime) / 300;

            if (morphPos >= 1) {
                morphPos = 1;
                this._phaseId++;
                this._phaseTime = TIMER.time;
            }
            else {
                morphPos = pc.CubicOut(morphPos);
            }
        }
        else {
            // Smer z 1 do 0
            morphPos = (TIMER.time - this._phaseTime) / 2500;

            if (morphPos >= 1) {
                this.reset();
                return false;
            }

            morphPos = 1 - pc.ElasticOut(morphPos, 0.3, 0.2);
        }

        this._zones.forEach((element: HitZoneMorph): void => {
            this._morphInstance.setWeight(element.zoneId, morphPos * element.weight * this._weightRatio);
        });

        return true;
    }
}



export class NetImpactSimulation {
    /**
     * Nastaveni vahy pro jednotlive hit zony zadni side (X0, vaha, ..., Xn, vaha)
     */
    private static readonly BACK_SIDE_HIT_ZONES: number[][] = [
        [0, 1, 0.25, 1, 0.375, 0.7, 0.5, 0],
        [0.25, 0, 0.375, 0.7, 0.5, 1, 0.625, 0.7, 0.75, 0],
        [0.5, 0, 0.625, 0.7, 0.75, 1, 1, 1]
    ];

    private _morpInstance: pc.MorphInstance;
    private _freeMorphs: Pool<MorphManager>;
    private _actMorphs: NodeList<MorphManager>;
    private _actGoalWallMask: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(netMorhInstance: pc.MorphInstance) {
        this._morpInstance = netMorhInstance;
        this._freeMorphs = new Pool<MorphManager>(undefined, 1, true, (): MorphManager => {
            return new MorphManager(this._morpInstance);
        });
        this._actMorphs = new NodeList<MorphManager>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        if (!this._actMorphs.empty) {
            this._actMorphs.forEach((morp: MorphManager): void => {
                morp.reset();
                this._freeMorphs.returnItem(morp);
            });
        }

        this._actGoalWallMask = 0;
    }

    //-------------------------------------------------------------------------
    /**
     * Zahajit simulaci narazu do bocni steny
     * @param impactPos 
     * @param impactImpulse 
     */
    //-------------------------------------------------------------------------
    startSideWallImpact(impactPos: pc.Vec3, impactImpulse: number): void {
        if ((this._actGoalWallMask & (1 << eGoalWall.side)) != 0 || impactImpulse < 1)
            // Opakovany nebo maly impuls ignorovat
            return;

        // Vybrat hit zonu
        let morph = this._freeMorphs.getItem();
        let zones: HitZoneMorph[] = [];

        zones.push({ zoneId: impactPos.x < 0 ? 3 : 4, weight: 0.25 });

        this._actGoalWallMask |= (1 << eGoalWall.side);
        morph.start(eGoalWall.side, zones, Math.min(1, impactImpulse / 4));
        this._actMorphs.add(morph);
    }

    //-------------------------------------------------------------------------
    /**
     * Zahajit simulaci narazu do zadni steny
     * @param impactPos 
     * @param impactImpulse 
     */
    //-------------------------------------------------------------------------
    startBackWallImpact(impactPos: pc.Vec3, impactImpulse: number): void {
        if ((this._actGoalWallMask & (1 << eGoalWall.back)) != 0 || impactImpulse < 1)
            // Opakovany nebo maly impuls ignorovat
            return;

        // Prevest impact pozici (na ose X) na 0 (levy okraj brany) az 1 (pravy okraj brany)
        let pos = (impactPos.x - -Stadium.GOAL_WIDTH / 2) / Stadium.GOAL_WIDTH;

        // Najit zony, do kterych naraz zasahuje
        let zones: HitZoneMorph[] = [];

        NetImpactSimulation.BACK_SIDE_HIT_ZONES.every((curveSet: number[], zoneId: number): boolean => {
            if (pos < curveSet[0])
                return false;

            if (pos <= curveSet[curveSet.length - 2]) {
                // Urcit vahu zony
                let i = curveSet.length - 2;

                while (i >= 0 && pos < curveSet[i]) {
                    i -= 2;
                }

                let weight: number;

                if (i + 2 < curveSet.length) {
                    weight = curveSet[i + 1] + ((pos - curveSet[i]) / (curveSet[i + 2] - curveSet[i])) * (curveSet[i + 3] - curveSet[i + 1]);
                }
                else {
                    weight = curveSet[i + 1];
                }

                zones.push({ zoneId: zoneId, weight: weight });
            }

            return true;
        });

        if (zones.length == 0)
            return;

        this._actGoalWallMask |= (1 << eGoalWall.back);

        // Spustit simulaci
        let morph = this._freeMorphs.getItem();

        morph.start(eGoalWall.back, zones, Math.min(1, Math.min(2, impactImpulse / 3)));
        this._actMorphs.add(morph);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        this._actMorphs.forEach((morph: MorphManager, node: Node<MorphManager>): void => {
            if (!morph.update()) {
                this._actGoalWallMask &= ~(1 << morph.goalWall);
                this._actMorphs.removeByNode(node);
                this._freeMorphs.returnItem(morph);
            }
        });
    }
}